import { DataManager } from '../core/data-manager';

/**
 * Allegati dei regolamenti: PDF o documento Word.
 *
 * Il caricamento e' quello del framework (`/resource/uploadFile`): il file
 * finisce in `upload/<entita>/<hash>.<ext>` e nella tabella `resource`, e
 * l'entita' ne tiene solo l'hash. In lettura l'API restituisce gia' la riga
 * risolta, quindi nome ed estensione arrivano senza chiamate in piu'.
 */

export const ESTENSIONI = ['pdf', 'doc', 'docx'];
export const PESO_MASSIMO = 5 * 1024 * 1024;

const MIME = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

export function estensioneDi(nome) {
    const punto = String(nome || '').lastIndexOf('.');
    return punto < 0 ? '' : String(nome).slice(punto + 1).toLowerCase();
}

/** Peso in forma leggibile, per dirlo accanto al nome del file. */
export function pesoLeggibile(byte) {
    const n = parseInt(byte, 10) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Controlla un file scelto dall'utente prima di spedirlo: gli stessi limiti
 * che applica il backend, detti pero' subito invece che dopo l'upload.
 * Restituisce il messaggio d'errore, oppure null se va bene.
 */
export function erroreFile(file) {
    if (!file) return 'Nessun file selezionato.';
    if (!ESTENSIONI.includes(estensioneDi(file.name))) {
        return `Formato non ammesso: sono accettati ${ESTENSIONI.join(', ')}.`;
    }
    if (file.size > PESO_MASSIMO) {
        return `Il file supera il limite di ${pesoLeggibile(PESO_MASSIMO)}.`;
    }
    return null;
}

/** Legge un File del browser e ne restituisce il base64 puro, senza prefisso. */
export function leggiBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const risultato = String(reader.result || '');
            const virgola = risultato.indexOf(',');
            resolve(virgola >= 0 ? risultato.slice(virgola + 1) : risultato);
        };
        reader.onerror = () => reject(new Error('Lettura del file non riuscita.'));
        reader.readAsDataURL(file);
    });
}

function base64ToBlob(base64, tipo) {
    const binario = window.atob(base64);
    const byte = new Uint8Array(binario.length);
    for (let i = 0; i < binario.length; i++) byte[i] = binario.charCodeAt(i);
    return new Blob([byte], { type: tipo || 'application/octet-stream' });
}

/**
 * Apre un allegato gia' caricato.
 *
 * Il file si ricompone da base64 in un blob locale invece di puntare alla
 * cartella `upload`: il link che l'API restituisce e' costruito sul dominio
 * scritto in settings.json, che arriva senza schema e non e' navigabile.
 *
 * Il PDF si apre in una scheda, il Word il browser puo' solo scaricarlo.
 */
export async function apriAllegato(allegato) {
    if (!allegato || !allegato.hash) throw new Error('Allegato non disponibile.');

    const res = await DataManager.getFile(allegato.hash);
    if (!res || res.success !== 1 || !res.base64) {
        throw new Error((res && res.message) || 'Allegato non recuperabile.');
    }

    const ext = String(allegato.extension || estensioneDi(allegato.filename)).toLowerCase();
    const url = URL.createObjectURL(base64ToBlob(res.base64, MIME[ext]));

    const a = document.createElement('a');
    a.href = url;
    if (ext === 'pdf') {
        a.target = '_blank';
        a.rel = 'noopener';
    }
    else {
        // Word: la scheda non lo mostrerebbe, tanto vale scaricarlo
        a.download = allegato.filename || `regolamento.${ext}`;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // il blob resta vivo finche' la scheda non l'ha aperto
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
